import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Instagram, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICES } from "@/data/content";

const WHATSAPP = "919730527427";
const INSTAGRAM = "https://www.instagram.com/digital.madhuraa";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goto = (href) => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-[#0F172A] text-slate-300 overflow-hidden" data-testid="footer">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ background: "#6C2BD9" }} />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full opacity-10 blur-3xl" style={{ background: "#2563EB" }} />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <img src="/logo.jpeg" alt="Digital Madhuraa" className="w-10 h-10 rounded-xl object-cover" style={{ objectPosition: "center 40%" }} />
              <span className="font-head font-extrabold text-xl text-white">Digital Madhuraa</span>
            </div>
            <p className="text-slate-400 leading-relaxed max-w-xs mb-6">
              A results-driven digital marketing agency helping brands grow through creativity, strategy and measurable performance.
            </p>
            <form onSubmit={subscribe} className="flex items-center gap-2 max-w-sm" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for insights"
                data-testid="footer-newsletter-input"
                className="flex-1 rounded-full bg-white/10 border border-white/15 px-4 py-2.5 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-[var(--dm-orange)]"
              />
              <button type="submit" disabled={loading} data-testid="footer-newsletter-submit" className="rounded-full w-11 h-11 grid place-items-center text-white shrink-0 transition-transform hover:-translate-y-0.5" style={{ background: "#F97316" }}>
                <ArrowRight size={18} />
              </button>
            </form>
          </div>

          <div>
            <h4 className="font-head font-bold text-white mb-4">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              {[["About", "#about"], ["Why Us", "#why"], ["Work", "#portfolio"], ["Pricing", "#pricing"], ["FAQ", "#faq"]].map(([l, h]) => (
                <li key={l}><button onClick={() => goto(h)} className="hover:text-white transition-colors">{l}</button></li>
              ))}
              <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-head font-bold text-white mb-4">Services</h4>
            <ul className="space-y-3 text-sm">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.key}><button onClick={() => goto("#services")} className="hover:text-white transition-colors text-left">{s.title}</button></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-head font-bold text-white mb-4">Get in Touch</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3"><Mail size={18} className="text-[var(--dm-orange)] mt-0.5" /> <a href="mailto:madhuraadigital16@gmail.com" className="hover:text-white break-all">madhuraadigital16@gmail.com</a></li>
              <li className="flex items-start gap-3"><Phone size={18} className="text-[var(--dm-orange)] mt-0.5" /> <a href="tel:+919730527427" className="hover:text-white">+91 97305 27427</a></li>
              <li className="flex items-start gap-3"><MapPin size={18} className="text-[var(--dm-orange)] mt-0.5" /> <span>Pune, Maharashtra, India</span></li>
            </ul>
            <div className="flex gap-3 mt-5">
              <a href={INSTAGRAM} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="w-10 h-10 rounded-full grid place-items-center bg-white/10 hover:bg-[var(--dm-purple)] transition-colors">
                <Instagram size={18} />
              </a>
              <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="w-10 h-10 rounded-full grid place-items-center bg-white/10 hover:bg-green-500 transition-colors">
                <Phone size={18} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-400">
          <p>© {new Date().getFullYear()} Digital Madhuraa. All rights reserved.</p>
          <div className="flex gap-6">
            <button className="hover:text-white transition-colors">Privacy Policy</button>
            <button className="hover:text-white transition-colors">Terms &amp; Conditions</button>
            <Link to="/admin" className="hover:text-white transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
